/**
 * AuditLogDetail — expandable inline panel for a single audit log row.
 * US-008 Step 3 — shows full details including I/O hashes, metadata JSON.
 * NFR-007: I/O hashes surfaced here.
 * WCAG 2.1 AA: labelled region, copy buttons have accessible labels.
 */
import { Link } from 'react-router-dom';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { AuditLog } from '@/types/index';

interface AuditLogDetailProps {
  log: AuditLog;
}

function HashField({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;

  const truncated = value.length > 16 ? `${value.slice(0, 16)}…` : value;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="flex items-center gap-2">
      <dt className="w-28 shrink-0 text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="flex items-center gap-1.5 font-mono text-xs text-foreground">
        <span title={value}>{truncated}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0"
          onClick={() => void handleCopy()}
          aria-label={`Copy full ${label}`}
        >
          <Copy className="h-3 w-3" aria-hidden="true" />
        </Button>
      </dd>
    </div>
  );
}

function MetadataField({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null) return null;
  return (
    <div className="flex items-start gap-2">
      <dt className="w-28 shrink-0 text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-xs text-foreground break-all">{value}</dd>
    </div>
  );
}

export function AuditLogDetail({ log }: AuditLogDetailProps) {
  const meta = log.metadata ?? {};
  const inputHash = typeof meta['input_hash'] === 'string' ? meta['input_hash'] : log.input_hash;
  const outputHash = typeof meta['output_hash'] === 'string' ? meta['output_hash'] : log.output_hash;
  const agentName = typeof meta['agent_name'] === 'string' ? meta['agent_name'] : null;
  const modelVersion = typeof meta['model_version'] === 'string' ? meta['model_version'] : null;
  const durationMs = typeof meta['duration_ms'] === 'number' ? meta['duration_ms'] : null;

  // Stage approval / review fields — lifted out of the raw-JSON dump so
  // the reviewer's actual note is visible without parsing JSON by eye.
  const stageName = typeof meta['stage'] === 'string' ? meta['stage'] : null;
  const reviewState = typeof meta['review_state'] === 'string' ? meta['review_state'] : null;
  const approvedAt = typeof meta['approved_at'] === 'string' ? meta['approved_at'] : null;
  const reviewerNote = typeof meta['reviewer_note'] === 'string' ? meta['reviewer_note'] : null;
  const backfilled = meta['_backfilled_from_agent_results'] === true;

  // Remaining metadata (excluding fields surfaced above) — the raw JSON
  // dump is the safety net for fields nobody bothered to label yet.
  const knownFields = new Set([
    'input_hash', 'output_hash', 'agent_name', 'model_version', 'duration_ms',
    'stage', 'review_state', 'approved_at', 'reviewer_note',
    '_hash', 'actor_id',
  ]);
  const extraMeta: Record<string, unknown> = Object.fromEntries(
    Object.entries(meta).filter(([k]) => !knownFields.has(k)),
  );
  const hasExtraMeta = Object.keys(extraMeta).length > 0;

  return (
    <section
      aria-label="Audit log entry details"
      className="bg-muted/30 border-t border-border px-6 py-4"
    >
      {reviewerNote && (
        <div className="mb-3 rounded-md border border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30 px-3 py-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-violet-700 dark:text-violet-300">
            Reviewer note{stageName ? ` — ${stageName}` : ''}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-violet-950 dark:text-violet-100">
            {reviewerNote}
          </p>
        </div>
      )}

      <dl className="space-y-2">
        <MetadataField label="Stage" value={stageName} />
        <MetadataField label="Review state" value={reviewState} />
        <MetadataField
          label="Approved at"
          value={approvedAt ? new Date(approvedAt).toLocaleString() : null}
        />
        <MetadataField label="Full Job ID" value={log.job_id} />
        <MetadataField label="Actor ID" value={log.actor_id} />
        <MetadataField label="Agent" value={agentName} />
        <MetadataField label="Model version" value={modelVersion} />
        {durationMs != null && (
          <div className="flex items-center gap-2">
            <dt className="w-28 shrink-0 text-xs font-medium text-muted-foreground">Duration</dt>
            <dd className="text-xs text-foreground">{durationMs.toLocaleString()} ms</dd>
          </div>
        )}
        <HashField label="Input hash" value={inputHash ?? null} />
        <HashField label="Output hash" value={outputHash ?? null} />
        <MetadataField
          label="Timestamp"
          value={new Date(log.created_at).toISOString()}
        />

        {hasExtraMeta && (
          <div className="flex items-start gap-2 pt-1">
            <dt className="w-28 shrink-0 text-xs font-medium text-muted-foreground">Metadata</dt>
            <dd className="flex-1">
              <pre className="rounded bg-muted px-3 py-2 text-xs overflow-x-auto">
                {JSON.stringify(extraMeta, null, 2)}
              </pre>
            </dd>
          </div>
        )}
      </dl>

      {backfilled && (
        <p className="mt-3 text-[11px] italic text-muted-foreground">
          Backfilled from agent_results — predates the audit-event-type fix.
        </p>
      )}

      {log.job_id && (
        <div className="mt-3 pt-3 border-t border-border/60">
          <Link
            to={`/jobs/${log.job_id}`}
            className="inline-flex items-center gap-1.5 text-xs text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
          >
            View Job
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      )}
    </section>
  );
}
